import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  Filter,
  FileSignature,
  Inbox,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { createAdminAgreement, getAdminAgreement, getAdminAgreements, getAdminProposals, updateAdminAgreement } from "../../lib/api";
import type { AdminAgreementDetail, AdminAgreementRecord, AdminProposalRecord } from "../../lib/types";
import { FormPortalHeader } from "./FormPortalHeader";

const agreementStatuses = ["All", "Draft", "Sent", "Viewed", "Signed", "Expired", "Cancelled"] as const;

export function AgreementRecordsPage() {
  const { agreementId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState<AdminAgreementRecord[]>([]);
  const [proposals, setProposals] = useState<AdminProposalRecord[]>([]);
  const [detail, setDetail] = useState<AdminAgreementDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(Boolean(agreementId));
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [createProposalId, setCreateProposalId] = useState("");
  const [agreementUrl, setAgreementUrl] = useState("");
  const [busyAction, setBusyAction] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<(typeof agreementStatuses)[number]>("All");

  useEffect(() => {
    document.title = agreementId ? "Agreement Detail | Business Center OS" : "Agreement Records | Business Center OS";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        "Shared Business Center OS agreement workspace for sent, viewed, and signed service agreements.",
      );
    void loadAgreements();
  }, [agreementId]);

  useEffect(() => {
    if (!agreementId) {
      setDetail(null);
      return;
    }
    void loadDetail(agreementId);
  }, [agreementId]);

  useEffect(() => {
    if (!location.hash) return;
    const target = document.getElementById(location.hash.slice(1));
    target?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [location.hash, detail, agreements]);

  async function loadAgreements() {
    setLoading(true);
    setError("");
    try {
      const [agreementData, proposalData] = await Promise.all([getAdminAgreements(), getAdminProposals()]);
      const data = agreementData;
      setAgreements(data.agreements || []);
      setProposals(proposalData.proposals || data.proposals || []);
      setCreateProposalId((current) => current || proposalData.proposals?.[0]?.id || data.proposals?.[0]?.id || "");
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load agreement records.");
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateAgreement() {
    if (!createProposalId) {
      setActionMessage("Select a proposal to create an agreement.");
      return;
    }
    setBusyAction(true);
    setActionMessage("");
    try {
      const result = await createAdminAgreement({
        proposalId: createProposalId,
        agreementUrl: agreementUrl.trim() || undefined,
      });
      setActionMessage(`Created ${result.agreement.agreementNumber} and opened the detail view.`);
      await loadAgreements();
      navigate(`/admin/agreement-records/${result.agreement.id}`);
    } catch (createError) {
      setActionMessage(createError instanceof Error ? createError.message : "Unable to create agreement.");
    } finally {
      setBusyAction(false);
    }
  }

  async function updateAgreementStatus(nextAction: "viewed" | "signed", signerName?: string, signerEmail?: string) {
    if (!detail) return;
    setBusyAction(true);
    setActionMessage("");
    try {
      const result = await updateAdminAgreement(detail.agreement.id, {
        action: nextAction,
        signerName,
        signerEmail,
      });
      setActionMessage(`Agreement marked as ${nextAction}.`);
      await loadAgreements();
      await loadDetail(detail.agreement.id);
      if (result.agreement?.id) {
        navigate(`/admin/agreement-records/${result.agreement.id}`);
      }
    } catch (updateError) {
      setActionMessage(updateError instanceof Error ? updateError.message : "Unable to update agreement status.");
    } finally {
      setBusyAction(false);
    }
  }

  async function loadDetail(targetAgreementId: string) {
    setLoadingDetail(true);
    setError("");
    try {
      const data = await getAdminAgreement(targetAgreementId);
      setDetail(data);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load agreement detail.");
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }

  const filteredAgreements = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return agreements.filter((agreement) => {
      const matchesStatus = statusFilter === "All" || agreement.displayStatus === statusFilter;
      const matchesQuery =
        !normalizedQuery ||
        [
          agreement.agreementNumber,
          agreement.id,
          agreement.leadName,
          agreement.leadEmail,
          agreement.eventName,
          agreement.proposalNumber,
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);
      return matchesStatus && matchesQuery;
    });
  }, [agreements, query, statusFilter]);

  const metrics = useMemo(() => {
    const total = agreements.length;
    const pendingSignature = agreements.filter((item) => item.displayStatus === "Sent" || item.displayStatus === "Viewed").length;
    const viewed = agreements.filter((item) => item.displayStatus === "Viewed").length;
    const signed = agreements.filter((item) => item.displayStatus === "Signed").length;
    const expired = agreements.filter((item) => item.displayStatus === "Expired").length;
    const cancelled = agreements.filter((item) => item.displayStatus === "Cancelled").length;
    return [
      { icon: FileSignature, label: "Total Agreements", value: total },
      { icon: Clock3, label: "Pending Signature", value: pendingSignature },
      { icon: Users, label: "Viewed", value: viewed },
      { icon: CheckCircle2, label: "Signed", value: signed },
      { icon: ShieldCheck, label: "Expired", value: expired },
      { icon: Inbox, label: "Cancelled", value: cancelled },
    ];
  }, [agreements]);

  return (
    <main className="command-center-shell min-h-screen text-[#fff9ef]">
      <FormPortalHeader
        brandHref="/admin/business-center-os"
        brandName="Business Center OS"
        brandSubtitle="Shared lifecycle records"
        brandLogoAlt="Business Center OS"
        adminDirectoryHref="/admin/business-center-os"
        adminDirectoryLabel="Back to Business Center OS"
        showPublicBackLink={false}
      />
      <Hero
        eyebrow="Business Center OS"
        title="Agreement Records Workspace"
        description="Track sent, viewed, and signed agreements; search by client, agreement ID, email, or event; and open the linked proposal or activity timeline from a shared platform surface."
      />

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
          <div id="agreement-status" className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
            {metrics.map((metric) => (
              <MetricCard key={metric.label} icon={metric.icon} label={metric.label} value={metric.value} />
            ))}
          </div>

          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <SectionHeading eyebrow="Agreement Creation" title="Create a new agreement record" />
            <p className="mt-3 max-w-3xl text-sm font-semibold leading-7 text-[#5c5246]">
              Select a proposal, optionally attach an agreement URL, and create a signed workflow record that links back
              to the lead, client, and proposal timeline.
            </p>
            <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
              <label className="grid gap-2 text-sm font-bold text-[#050505]">
                Proposal
                <select
                  value={createProposalId}
                  onChange={(event) => setCreateProposalId(event.target.value)}
                  className="command-center-field focus-ring min-h-12 rounded-lg px-4 py-3 text-sm font-semibold"
                >
                  <option value="">Select a proposal</option>
                  {proposals.map((proposal) => (
                    <option key={proposal.id} value={proposal.id}>
                      {proposal.proposalNumber} - {proposal.leadName}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#050505]">
                Agreement URL
                <input
                  value={agreementUrl}
                  onChange={(event) => setAgreementUrl(event.target.value)}
                  className="command-center-field focus-ring min-h-12 rounded-lg px-4 py-3 text-sm font-semibold"
                  placeholder="https://..."
                />
              </label>
              <button
                type="button"
                onClick={() => void handleCreateAgreement()}
                disabled={busyAction || !proposals.length}
                className="admin-primary-button focus-ring self-end disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busyAction ? "Creating..." : "Create Agreement"}
              </button>
            </div>
            {actionMessage ? <Notice tone="success" message={actionMessage} /> : null}
            {!proposals.length ? (
              <p className="mt-4 text-sm font-semibold leading-7 text-[#5c5246]">
                No proposals are available yet. Create a proposal first, then return here to generate the agreement
                record.
              </p>
            ) : null}
          </section>

          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <div className="flex flex-col gap-4 border-b border-[#D94A16]/16 pb-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Agreement Search</p>
                <h2 className="mt-1 font-display text-3xl font-bold text-[#050505]">Agreement Table</h2>
                <p className="mt-2 max-w-3xl text-sm font-semibold leading-7 text-[#5c5246]">
                  Search by client name, agreement ID, email address, or event name.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <button type="button" onClick={() => void loadAgreements()} className="admin-secondary-button focus-ring" title="Refresh agreement records">
                  Refresh
                </button>
                <Link to="/admin/business-center-os" className="admin-primary-button focus-ring" title="Back to Business Center OS">
                  Back to Business Center OS
                </Link>
              </div>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_280px]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#D94A16]" aria-hidden="true" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-semibold"
                  placeholder="Search client, agreement ID, email, event, or proposal"
                />
              </label>
              <label className="relative block">
                <Filter className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#D94A16]" aria-hidden="true" />
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value as (typeof agreementStatuses)[number])}
                  className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-bold"
                >
                  {agreementStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {error ? <Notice tone="error" message={error} /> : null}
            {loading ? <EmptyState title="Loading agreements" message="Checking the agreement records workspace for recent signatures and client activity." /> : null}
            {!loading && !error && !filteredAgreements.length ? (
              <EmptyState
                title="No agreement records yet"
                message="Your first agreement will appear here after a proposal is converted into a client signature workflow."
              />
            ) : null}

            <div className="mt-6 overflow-hidden rounded-lg border border-[#D94A16]/16">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#D94A16]/14 text-left">
                  <thead className="bg-black/20 text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                    <tr>
                      <Th>Agreement ID</Th>
                      <Th>Client</Th>
                      <Th>Event</Th>
                      <Th>Proposal Linked</Th>
                      <Th>Date Sent</Th>
                      <Th>Date Viewed</Th>
                      <Th>Date Signed</Th>
                      <Th>Status</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#D94A16]/10 bg-white/5">
                    {filteredAgreements.map((agreement) => (
                      <tr key={agreement.id} className="transition hover:bg-white/8">
                        <Td className="font-black text-[#fff9ef]">{agreement.agreementNumber}</Td>
                        <Td>
                          <div className="grid">
                            <span className="font-bold text-[#fff9ef]">{agreement.leadName}</span>
                            <span className="text-xs font-semibold text-[#fff9ef]/68">{agreement.leadEmail || "No email"}</span>
                          </div>
                        </Td>
                        <Td className="text-[#fff9ef]/82">{agreement.eventName || "Event not set"}</Td>
                        <Td>
                          <Link
                            to={`/admin/proposals#proposal-records`}
                            className="inline-flex items-center gap-2 rounded-full border border-[#FF6A2B]/25 px-3 py-1 text-xs font-black text-[#FF6A2B] transition hover:border-[#FF6A2B] hover:bg-[#FF6A2B]/12"
                          >
                            {agreement.proposalNumber}
                            <ArrowRight className="size-3" aria-hidden="true" />
                          </Link>
                        </Td>
                        <Td>{formatDate(agreement.sentAt)}</Td>
                        <Td>{formatDate(agreement.viewedAt)}</Td>
                        <Td>{formatDate(agreement.signedAt)}</Td>
                        <Td>
                          <StatusPill status={agreement.displayStatus} />
                        </Td>
                        <Td>
                          <Link
                            to={`/admin/agreement-records/${agreement.id}`}
                            className="inline-flex items-center gap-2 rounded-full border border-[#FF6A2B]/25 px-3 py-1 text-xs font-black text-[#FF6A2B] transition hover:border-[#FF6A2B] hover:bg-[#FF6A2B]/12"
                          >
                            View detail
                            <ArrowRight className="size-3" aria-hidden="true" />
                          </Link>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {loadingDetail ? <EmptyState title="Loading agreement detail" message="Opening the agreement detail page and loading the linked proposal, signature history, and activity log." /> : null}

          {detail ? (
            <section className="grid gap-6">
              <article className="command-center-card rounded-lg p-5 sm:p-6">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Agreement Detail</p>
                <h2 className="mt-2 font-display text-3xl font-bold text-[#050505]">{detail.agreement.agreementNumber || agreementTitle(detail.agreement.token)}</h2>
                <p className="mt-2 text-sm font-semibold leading-7 text-[#5c5246]">
                  Review the agreement record, linked proposal, signature history, and activity timeline for this client.
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => void updateAgreementStatus("viewed")}
                    disabled={busyAction}
                    className="admin-secondary-button focus-ring disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Mark Viewed
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      void updateAgreementStatus(
                        "signed",
                        detail.agreement.signerName || detail.lead?.fullName || undefined,
                        detail.agreement.signerEmail || detail.lead?.email || undefined,
                      )
                    }
                    disabled={busyAction}
                    className="admin-primary-button focus-ring disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Mark Signed
                  </button>
                  {actionMessage ? <span className="self-center text-sm font-semibold text-[#D94A16]">{actionMessage}</span> : null}
                </div>
                <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <InfoCard label="Client" value={detail.lead?.fullName || "Unknown"} />
                  <InfoCard label="Email" value={detail.lead?.email || "Unknown"} />
                  <InfoCard label="Proposal" value={detail.proposal?.proposal.proposalNumber || "Not linked"} />
                  <InfoCard label="Agreement Status" value={detail.agreement.status} />
                </div>
              </article>

              <article id="signature-details" className="command-center-card rounded-lg p-5 sm:p-6">
                <SectionHeading eyebrow="Signature Details" title="Agreement information and signature history" />
                <div className="mt-5 grid gap-4 lg:grid-cols-2">
                  <DetailBlock
                    title="Agreement Information"
                    items={[
                      ["Agreement ID", detail.agreement.agreementNumber || agreementTitle(detail.agreement.token)],
                      ["Provider", detail.agreement.provider],
                      ["Agreement URL", detail.agreement.agreementUrl],
                      ["Sent", formatDate(detail.agreement.sentAt)],
                      ["Viewed", formatDate(detail.agreement.viewedAt)],
                      ["Signed", formatDate(detail.agreement.signedAt)],
                    ]}
                  />
                  <DetailBlock
                    title="Linked Client & Proposal"
                    items={[
                      ["Client", detail.lead?.fullName || "Unknown"],
                      ["Email", detail.lead?.email || "Unknown"],
                      ["Event", detail.lead?.eventType || "Unknown"],
                      ["Proposal", detail.proposal?.proposal.proposalNumber || "Not linked"],
                      ["Package", detail.proposal?.proposal.packageName || "Not linked"],
                      ["Total", detail.proposal ? formatMoney(detail.proposal.proposal.totalCents) : "—"],
                    ]}
                  />
                </div>
              </article>

              <article className="command-center-card rounded-lg p-5 sm:p-6">
                <SectionHeading eyebrow="Agreement Timeline" title="Created, sent, viewed, reminder, signed, and completed" />
                <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {buildAgreementTimeline(detail).map((item) => (
                    <TimelineCard key={item.label} label={item.label} value={item.value} active={item.active} />
                  ))}
                </div>
              </article>

              <article className="command-center-card rounded-lg p-5 sm:p-6">
                <SectionHeading eyebrow="Activity Log" title="Recent agreement activity" />
                <div className="mt-5 grid gap-3">
                  {detail.timeline.length ? (
                    detail.timeline.map((item) => (
                      <div key={item.id} className="rounded-lg border border-[#FF6A2B]/16 bg-white/5 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <p className="font-black text-[#fff9ef]">{item.activityType}</p>
                          <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{formatDate(item.createdAt)}</p>
                        </div>
                        <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/74">{item.notes || "No notes recorded."}</p>
                        {item.relatedLink ? (
                          <a
                            href={item.relatedLink}
                            className="mt-3 inline-flex items-center gap-2 text-sm font-black text-[#FF6A2B] underline decoration-[#FF6A2B] decoration-2 underline-offset-4"
                          >
                            Open related record
                            <ArrowRight className="size-4" aria-hidden="true" />
                          </a>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <EmptyState title="No agreement activity yet" message="Activity entries will appear as the agreement is viewed, signed, or updated." />
                  )}
                </div>
              </article>

              <article className="command-center-card rounded-lg p-5 sm:p-6">
                <SectionHeading eyebrow="Related Records" title="Lead and proposal context" />
                <div className="mt-5 grid gap-4 md:grid-cols-4">
                  <RecordCard
                    label="Lead"
                    value={detail.lead?.fullName || "Unknown"}
                    helper={detail.lead?.email || "No email available"}
                    href={detail.lead?.id ? `/admin/leads/${detail.lead.id}` : "/admin/leads"}
                  />
                  <RecordCard
                    label="Client"
                    value={detail.proposal?.proposal.clientId ? "Linked" : "Not linked"}
                    helper={detail.proposal?.proposal.clientId ? "Open the linked client record" : "Open the client workspace"}
                    href={detail.proposal?.proposal.clientId ? `/admin/forms-client/${detail.proposal.proposal.clientId}` : "/admin/forms-client-directory"}
                  />
                  <RecordCard
                    label="Proposal"
                    value={detail.proposal?.proposal.proposalNumber || "Not linked"}
                    helper={detail.proposal?.proposal.packageName || "Open the proposal workspace"}
                    href="/admin/proposals#proposal-records"
                  />
                  <RecordCard
                    label="Payment"
                    value={detail.payment?.invoiceStatus || detail.payment?.status || "No payment yet"}
                    helper={detail.payment?.stripeCheckoutUrl || "Payment record not created yet"}
                    href={detail.payment ? `/admin/payment-records/${detail.payment.id}` : "/admin/payment-records"}
                  />
                </div>
              </article>
            </section>
          ) : null}
        </div>
      </section>
    </main>
  );
}

function Hero({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return (
    <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,106,43,.18),transparent_34%),linear-gradient(120deg,rgba(5,5,5,.96),rgba(22,18,10,.9))]" />
      <div className="relative mx-auto grid max-w-6xl gap-5">
        <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#FF6A2B]/35 px-3 py-2 text-xs font-black uppercase tracking-[0.2em] text-[#FF6A2B]">
          <FileSignature className="size-4" aria-hidden="true" />
          {eyebrow}
        </div>
        <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">{title}</h1>
        <p className="max-w-3xl text-base font-semibold leading-8 text-[#fff9ef]/76">{description}</p>
      </div>
    </section>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Inbox;
  label: string;
  value: number;
}) {
  return (
    <article className="command-center-card rounded-lg p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{label}</p>
          <p className="mt-2 font-display text-3xl font-bold text-[#050505]">{value}</p>
        </div>
        <span className="grid size-11 place-items-center rounded-lg bg-[#050505] text-[#FF6A2B]">
          <Icon className="size-5" aria-hidden="true" />
        </span>
      </div>
    </article>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "Signed"
      ? "bg-emerald-500/18 text-emerald-200 border-emerald-400/30"
      : status === "Viewed"
        ? "bg-sky-500/18 text-sky-200 border-sky-400/30"
        : status === "Sent"
          ? "bg-[#FF6A2B]/12 text-[#FF6A2B] border-[#FF6A2B]/30"
          : status === "Expired"
            ? "bg-orange-500/18 text-orange-200 border-orange-400/30"
            : status === "Cancelled"
              ? "bg-rose-500/18 text-rose-200 border-rose-400/30"
              : "bg-white/8 text-[#fff9ef] border-[#FF6A2B]/18";
  return <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-black uppercase tracking-[0.14em] ${tone}`}>{status}</span>;
}

function SectionHeading({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div>
      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{eyebrow}</p>
      <h3 className="mt-2 font-display text-2xl font-bold text-[#050505]">{title}</h3>
    </div>
  );
}

function DetailBlock({ title, items }: { title: string; items: Array<[string, string | undefined | null]> }) {
  return (
    <div className="rounded-lg border border-[#D94A16]/16 bg-white/5 p-4">
      <p className="text-sm font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{title}</p>
      <dl className="mt-4 grid gap-3">
        {items.map(([label, value]) => (
          <div key={label} className="grid gap-1">
            <dt className="text-xs font-black uppercase tracking-[0.14em] text-[#fff9ef]/58">{label}</dt>
            <dd className="break-words text-sm font-semibold leading-7 text-[#fff9ef]">{value || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function TimelineCard({ label, value, active }: { label: string; value: string; active: boolean }) {
  return (
    <div className={`rounded-lg border p-4 ${active ? "border-[#FF6A2B]/45 bg-[#FF6A2B]/10" : "border-[#D94A16]/16 bg-white/5"}`}>
      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{label}</p>
      <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]">{value}</p>
    </div>
  );
}

function RecordCard({ label, value, helper, href }: { label: string; value: string; helper: string; href: string }) {
  return (
    <Link
      to={href}
      className="group grid gap-2 rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4 transition hover:-translate-y-0.5 hover:border-[#FF6A2B]/55 hover:bg-white/10"
    >
      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{label}</p>
      <p className="font-black text-[#fff9ef]">{value}</p>
      <p className="text-sm font-semibold leading-7 text-[#fff9ef]/68">{helper}</p>
      <span className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
        Open record
        <ArrowRight className="size-4 transition group-hover:translate-x-1" aria-hidden="true" />
      </span>
    </Link>
  );
}

function InfoCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-[#D94A16]/16 bg-white/5 p-4">
      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{label}</p>
      <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]">{value || "—"}</p>
    </div>
  );
}

function EmptyState({ title, message }: { title: string; message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-[#D94A16]/24 bg-white/4 p-6 text-center">
      <p className="text-sm font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{title}</p>
      <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/72">{message}</p>
    </div>
  );
}

function Notice({ tone, message }: { tone: "error" | "success"; message: string }) {
  const classes =
    tone === "error"
      ? "border-red-300 bg-red-50 text-red-800"
      : "border-[#FF6A2B]/30 bg-white/5 text-[#fff9ef]";
  return <p className={`rounded-lg border px-5 py-4 text-sm font-bold ${classes}`}>{message}</p>;
}

function Th({ children }: { children: string }) {
  return <th className="px-4 py-4 align-top">{children}</th>;
}

function Td({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <td className={`px-4 py-4 align-top text-sm font-semibold text-[#fff9ef]/72 ${className}`}>{children}</td>;
}

function formatMoney(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((Number(cents) || 0) / 100);
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function buildAgreementTimeline(detail: AdminAgreementDetail) {
  const reminder = detail.timeline.find((item) => item.activityType.toLowerCase().includes("reminder"));
  const completed = detail.agreement.signedAt || detail.agreement.updatedAt;
  return [
    { label: "Created", value: formatDate(detail.agreement.createdAt), active: Boolean(detail.agreement.createdAt) },
    { label: "Sent", value: formatDate(detail.agreement.sentAt), active: Boolean(detail.agreement.sentAt) },
    { label: "Viewed", value: formatDate(detail.agreement.viewedAt), active: Boolean(detail.agreement.viewedAt) },
    { label: "Reminder Sent", value: reminder ? formatDate(reminder.createdAt) : "Not yet sent", active: Boolean(reminder) },
    { label: "Signed", value: formatDate(detail.agreement.signedAt), active: Boolean(detail.agreement.signedAt) },
    { label: "Completed", value: formatDate(completed), active: Boolean(detail.agreement.signedAt) },
  ];
}

function agreementTitle(token: string) {
  return `AG-${token.slice(0, 8).toUpperCase()}`;
}

// Type-only helper to satisfy React.ReactNode without importing React.
type ReactNode = import("react").ReactNode;
